import type { definitions } from '@luxuryescapes/contract-search';

import type { AdminSearchCacheClearBody, SearchReproductionRow } from '~/services/ReproduceSearchService';

import { filterLePlaceIds, isGoogleBoundsUrlValue } from '~/utils/lePlaceId';

type HotelSortBy = definitions['HotelSortBy'];

type BuildSearchCacheClearBodyResult = { ok: true; body: AdminSearchCacheClearBody } | { ok: false; reason: string };

type CsvFilterField = keyof Pick<
  AdminSearchCacheClearBody,
  | 'amenities'
  | 'inclusions'
  | 'holidayTypes'
  | 'paymentOptions'
  | 'propertyTypes'
  | 'luxPlusExclusive'
  | 'campaigns'
  | 'societe'
>;

type NumericFilterField = keyof Pick<
  AdminSearchCacheClearBody,
  'customerRatingGte' | 'starRatingGte' | 'priceGte' | 'priceLte'
>;

type FilterMapping = { kind: 'csv'; field: CsvFilterField } | { kind: 'numeric'; field: NumericFilterField };

const DEFAULT_ADULTS = 2;
const DEFAULT_CHILDREN = 0;

const HOTEL_SORT_BY_VALUES = [
  'recommended',
  'distance',
  'value',
  'homepage',
  'price.asc',
  'price.desc',
  'points.asc',
  'points.desc',
  'sales',
  'endingsoon',
  'newest',
  'expectedValue.3',
  'expectedValue.7',
  'expectedValue.15',
  'expectedValue.30',
  'customerRating',
  'inclusions',
  'vertical',
  'commercialFirst',
  'luxPlusList',
  'type',
] as const satisfies ReadonlyArray<HotelSortBy>;

const FILTER_MAPPINGS: Record<string, FilterMapping> = {
  amenities: { kind: 'csv', field: 'amenities' },
  minimum_customer_rating: { kind: 'numeric', field: 'customerRatingGte' },
  inclusions: { kind: 'csv', field: 'inclusions' },
  holiday_types: { kind: 'csv', field: 'holidayTypes' },
  star_rating: { kind: 'numeric', field: 'starRatingGte' },
  payment_options: { kind: 'csv', field: 'paymentOptions' },
  property_types: { kind: 'csv', field: 'propertyTypes' },
  lux_plus_exclusive: { kind: 'csv', field: 'luxPlusExclusive' },
  campaigns: { kind: 'csv', field: 'campaigns' },
  societe: { kind: 'csv', field: 'societe' },
  price_gte: { kind: 'numeric', field: 'priceGte' },
  price_lte: { kind: 'numeric', field: 'priceLte' },
};

function encodeOccupancy(adults: number | null, children: number | null): string {
  const adultCount = adults ?? DEFAULT_ADULTS;
  const childCount = children ?? DEFAULT_CHILDREN;

  if (childCount <= 0) {
    return String(adultCount);
  }

  return `${adultCount}-${Array(childCount).fill(0).join(',')}`;
}

function isFlexibleNightsValue(value: string): boolean {
  const segments = value.split('-');
  if (segments.length !== 2) {
    return false;
  }

  const start = Number(segments[0]);
  const end = Number(segments[1]);

  return Number.isInteger(start) && Number.isInteger(end) && start < end;
}

function isHotelSortBy(value: string): value is HotelSortBy {
  return (HOTEL_SORT_BY_VALUES as ReadonlyArray<string>).includes(value);
}

function applyFilters(body: AdminSearchCacheClearBody, filters: SearchReproductionRow['filters']) {
  const csvValues: Partial<Record<CsvFilterField, Array<string>>> = {};

  filters.forEach((filter) => {
    const mapping = FILTER_MAPPINGS[filter.type ?? ''];
    const rawValue = filter.value?.trim();

    if (!mapping || !rawValue) {
      return;
    }

    if (mapping.kind === 'numeric') {
      const numericValue = Number(rawValue);
      if (!Number.isNaN(numericValue)) {
        body[mapping.field] = numericValue;
      }
      return;
    }

    const values = csvValues[mapping.field] ?? [];
    values.push(rawValue);
    csvValues[mapping.field] = values;
  });

  (Object.entries(csvValues) as Array<[CsvFilterField, Array<string>]>).forEach(([field, values]) => {
    body[field] = values.join(',');
  });
}

function resolveSearchTarget(row: SearchReproductionRow): Partial<AdminSearchCacheClearBody> | null {
  const naturalLanguageQuery = row.naturalLanguageQuery?.trim();
  if (naturalLanguageQuery) {
    return { query: naturalLanguageQuery };
  }

  if (row.properties.length === 1) {
    return { propertyId: row.properties[0].id };
  }

  const boundsDestination = row.destinations.find((destination) => isGoogleBoundsUrlValue(destination.id));
  if (boundsDestination) {
    return { bounds: boundsDestination.id };
  }

  const placeIds = filterLePlaceIds([
    ...row.destinations.map((entity) => entity.id),
    ...row.landmarks.map((entity) => entity.id),
  ]);
  if (placeIds.length > 0) {
    return { placeIds: placeIds.join(',') };
  }

  const entityWithLocation = [...row.destinations, ...row.landmarks, ...row.properties].find(
    (entity) => entity.location,
  );
  if (entityWithLocation?.location) {
    return {
      lat: entityWithLocation.location.latitude,
      lng: entityWithLocation.location.longitude,
    };
  }

  return null;
}

export function buildSearchCacheClearBody(row: SearchReproductionRow): BuildSearchCacheClearBodyResult {
  if (!row.brand) {
    return { ok: false, reason: 'Brand is required to reset cache' };
  }

  if (!row.region) {
    return { ok: false, reason: 'Region is required to reset cache' };
  }

  const target = resolveSearchTarget(row);
  if (!target) {
    return { ok: false, reason: 'Search target is missing from reproduced metadata' };
  }

  const body: AdminSearchCacheClearBody = {
    brand: row.brand as AdminSearchCacheClearBody['brand'],
    region: row.region as AdminSearchCacheClearBody['region'],
    occupancy: [encodeOccupancy(row.adults, row.children)],
    ...target,
  };

  if (row.dateType === 'specific' && row.startDate && row.endDate) {
    body.checkIn = row.startDate;
    body.checkOut = row.endDate;
  }

  if (row.flexibleMonths.length > 0) {
    body.flexibleMonths = row.flexibleMonths.join(',');
  }

  if (row.durationType && isFlexibleNightsValue(row.durationType)) {
    body.flexibleNights = row.durationType;
  }

  if (row.sortValue && isHotelSortBy(row.sortValue)) {
    body.sortBy = row.sortValue;
  }

  if (row.language) {
    body.lang = row.language;
  }

  applyFilters(body, row.filters);

  return { ok: true, body };
}
